import { HTTP_URL } from "../../config/backend";
import { useEffect, useState, useRef } from "react";

const BACKEND = HTTP_URL;
const IMG_BASE = `${BACKEND}/static/quests`;

function QuestImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError]   = useState(false);

  if (!src || error) {
    return (
      <div style={{
        width: "100%", height: "100%", minHeight: "200px",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0a0a1a", borderRadius: "1rem",
        border: "2px solid #ffffff0a", color: "#333", fontSize: "2.5rem",
      }}>🔍</div>
    );
  }

  const url = src.startsWith("http") ? src : `${IMG_BASE}/${src}`;
  return (
    <div style={{
      width: "100%", height: "100%", minHeight: "200px",
      borderRadius: "1rem", overflow: "hidden",
      border: "2px solid #ffffff11", background: "#0a0a1a",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {!loaded && <div style={{ color: "#333", fontSize: "2rem" }}>⏳</div>}
      <img
        src={url} alt={alt || ""}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        style={{
          width: "100%", height: "100%",
          objectFit: "contain",
          display: loaded ? "block" : "none",
        }}
      />
    </div>
  );
}

function VoteBar({ choices, voteCounts, totalPlayers, winningIndex, phase }) {
  const COLORS = ["#ff3cac", "#00f5ff", "#ffe600", "#3dffa0"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
      {choices.map((c, i) => {
        const count    = voteCounts[i] || 0;
        const pct      = totalPlayers > 0 ? Math.round((count / totalPlayers) * 100) : 0;
        const isWinner = phase === "result" && i === winningIndex;
        const color    = COLORS[i % COLORS.length];
        return (
          <div key={i} style={{
            background: isWinner ? color + "22" : "#ffffff08",
            border: `2px solid ${isWinner ? color : "#ffffff15"}`,
            borderRadius: "0.7rem", padding: "0.75rem 1rem",
            position: "relative", overflow: "hidden", transition: "all 0.3s",
          }}>
            <div style={{
              position: "absolute", left: 0, top: 0, bottom: 0,
              width: `${pct}%`, background: color + "1a",
              transition: "width 0.5s ease",
            }} />
            <div style={{
              position: "relative", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: "0.5rem",
            }}>
              <span style={{
                fontFamily: "'Nunito', sans-serif", fontSize: "1.05rem",
                color: isWinner ? "#fff" : "#bbb", fontWeight: isWinner ? 700 : 400,
                lineHeight: 1.4,
              }}>
                {isWinner && "✅ "}{c.text}
              </span>
              <span style={{
                fontFamily: "'Fredoka One', sans-serif", color,
                fontSize: "1rem", minWidth: "1.8rem", textAlign: "right", flexShrink: 0,
              }}>
                {count > 0 ? count : ""}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Timer({ seconds, running }) {
  const [left, setLeft] = useState(seconds);
  const ref = useRef(null);

  useEffect(() => {
    setLeft(seconds);
    if (!running) return;
    ref.current = setInterval(() => {
      setLeft(prev => { if (prev <= 1) { clearInterval(ref.current); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [seconds, running]);

  const pct   = seconds > 0 ? (left / seconds) * 100 : 0;
  const color = pct > 50 ? "#3dffa0" : pct > 20 ? "#ffe600" : "#ff3355";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
      <div style={{ flex: 1, height: "5px", background: "#ffffff11", borderRadius: "3px", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, transition: "width 1s linear, background 0.3s" }} />
      </div>
      <span style={{ fontFamily: "'Fredoka One', sans-serif", color, fontSize: "1rem", minWidth: "2.5rem", textAlign: "right" }}>
        {left}s
      </span>
    </div>
  );
}

export default function QuestHost({ questData, tv, onChoose }) {
  const fs = (a, b) => tv ? a : b;

  const phase        = questData?.phase ?? "waiting";
  const text         = questData?.text ?? "";
  const image        = questData?.image ?? null;
  const choices      = questData?.choices ?? [];
  const voteCounts   = questData?.vote_counts ?? {};
  const totalVoted   = questData?.total_voted ?? 0;
  const totalPlayers = questData?.total_players ?? 0;
  const winningIndex = questData?.winning_index ?? -1;
  const winningText  = questData?.winning_text ?? "";
  const step         = questData?.step ?? 0;
  const voteTime     = questData?.vote_time ?? 45;
  const scores       = questData?.scores ?? {};
  const endTitle     = questData?.title ?? "";

  if (phase === "waiting") {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🔍</div>
        <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.3rem", color: "#9b5de5" }}>
          Квест начинается...
        </p>
      </div>
    );
  }

  if (phase === "ending") {
    return (
      <div style={{
        display: "flex", gap: fs("1.5rem", "1rem"),
        width: "100%", maxWidth: "900px", margin: "0 auto",
        padding: fs("1rem", "0.5rem"),
        flexDirection: image ? "row" : "column",
        alignItems: "flex-start", flexWrap: "wrap",
      }}>
        {image && (
          <div style={{ flex: "0 0 42%", minWidth: "200px", maxWidth: "42%" }}>
            <QuestImage src={image} alt="ending" />
          </div>
        )}
        <div style={{ flex: 1, minWidth: "200px", display: "flex", flexDirection: "column", gap: "1rem" }}>
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: fs("2rem", "1.4rem"), color: "#ffe600", margin: 0 }}>
            {endTitle}
          </p>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: fs("1rem", "0.88rem"), color: "#ccc", lineHeight: 1.65, margin: 0 }}>
            {text}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {Object.entries(scores).sort((a, b) => b[1] - a[1]).map(([name, score]) => (
              <div key={name} style={{
                background: "#ffffff0a", border: "1.5px solid #ffffff22",
                borderRadius: "0.6rem", padding: "0.3rem 0.7rem",
                fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-sm)", color: "#fff",
              }}>
                {name} <span style={{ color: "#ffe600" }}>{score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── VOTE / RESULT — two-column layout ────────────────────────────────────
  return (
    <div style={{ width: "100%", maxWidth: "960px", margin: "0 auto", padding: fs("1rem", "0.5rem") }}>

      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <span style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: fs("0.9rem", "0.75rem"), color: "#9b5de5" }}>
          🔍 Шаг {step + 1}
        </span>
        <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: fs("0.85rem", "0.72rem"), color: "#555" }}>
          {phase === "vote" ? `Проголосовало: ${totalVoted}/${totalPlayers}` : `Выбор: ${winningText}`}
        </span>
      </div>

      {/* Two columns */}
      <div style={{ display: "flex", gap: fs("1.25rem", "0.75rem"), alignItems: "stretch", flexWrap: "wrap" }}>

        {/* LEFT — image, takes 42% width, stretches full height */}
        <div style={{ flex: "0 0 42%", minWidth: "220px", display: "flex" }}>
          <QuestImage src={image} alt={`step-${step}`} />
        </div>

        {/* RIGHT — text + timer + choices */}
        <div style={{ flex: 1, minWidth: "240px", display: "flex", flexDirection: "column", gap: "0.85rem" }}>

          {/* Scene text */}
          <div style={{
            background: "#0d0d1f", border: "1.5px solid #ffffff11",
            borderRadius: "1rem", padding: fs("1.1rem", "0.75rem"),
          }}>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: fs("1rem", "0.88rem"),
              color: "#e0e0e0", lineHeight: 1.7, margin: 0,
            }}>
              {text}
            </p>
          </div>

          {/* Timer */}
          {phase === "vote" && choices.length > 0 && (
            <Timer seconds={voteTime} running={phase === "vote"} />
          )}

          {/* Choices */}
          {choices.length > 0 && (
            <div>
              {phase === "vote" && (
                <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-md)", color: "#555", marginBottom: "0.5rem" }}>
                  Игроки голосуют...
                </p>
              )}
              <VoteBar
                choices={choices}
                voteCounts={voteCounts}
                totalPlayers={totalPlayers}
                winningIndex={winningIndex}
                phase={phase}
              />
              {phase === "vote" && onChoose && (
                <div style={{ marginTop: "0.75rem" }}>
                  <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "0.72rem", color: "#444", marginBottom: "0.4rem" }}>
                    Выбрать за всех:
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                    {choices.map((c, i) => (
                      <button key={i} onClick={() => onChoose(i)} style={{
                        background: "#ffffff08", border: "1.5px solid #ffffff15",
                        borderRadius: "0.5rem", padding: "0.3rem 0.7rem",
                        fontFamily: "'Nunito', sans-serif", fontSize: "0.78rem",
                        color: "#777", cursor: "pointer",
                      }}>
                        {i + 1}. {c.text.length > 30 ? c.text.slice(0, 30) + "…" : c.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}